// @storybook-skip: internal section of LessonsExploreScreen; covered by its screen story
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { SectionList, type StyleProp, type ViewStyle } from "react-native";
import { useAppTheme } from "../../useAppTheme";
import Box from "../Box";
import { CustomListSubheader } from "../CustomListSubheader";
import EmptyState from "../EmptyView";
import { LessonListItem } from "../LessonsHomeScreen/LessonListItem";
import type { Lesson } from "../LessonsHomeScreen/types";
import { getExploreEmptyState } from "./helpers";
import type { ExploreFilterState, LessonSection } from "./types";

const ROW_HEIGHT = 136;

type Props = {
	sections: LessonSection[];
	onLessonPress: (lesson: Lesson) => void;
	onClearFilters: () => void;
	totalLessons: number;
	searchQuery: string;
	filterState: ExploreFilterState;
	contentContainerStyle?: StyleProp<ViewStyle>;
};

export const LessonsExploreList = ({
	sections,
	onLessonPress,
	onClearFilters,
	totalLessons,
	searchQuery,
	filterState,
	contentContainerStyle,
}: Props) => {
	const theme = useAppTheme();
	const { t, i18n } = useTranslation();
	const contentMaxWidth = theme.breakpoints?.sm ?? "100%";

	const wrapConstrainedContent = useCallback(
		(node: React.ReactNode) => (
			<Box alignSelf="center" maxWidth={contentMaxWidth} width="100%">
				{node}
			</Box>
		),
		[contentMaxWidth],
	);

	const renderItem = useCallback(
		({ item }: { item: Lesson }) =>
			wrapConstrainedContent(
				<Box paddingBottom={16}>
					<LessonListItem lesson={item} onPress={onLessonPress} />
				</Box>,
			),
		[onLessonPress, wrapConstrainedContent],
	);

	const renderSectionHeader = useCallback(
		({ section }: { section: LessonSection }) =>
			wrapConstrainedContent(
				<CustomListSubheader enableGutters={false}>
					{section.title}
				</CustomListSubheader>,
			),
		[wrapConstrainedContent],
	);

	const keyExtractor = useCallback(
		(item: Lesson, index: number) => `${item.id}-${index}`,
		[],
	);

	const emptyState = useMemo(() => {
		const visibleLessons = sections.reduce(
			(acc, section) => acc + section.data.length,
			0,
		);
		return getExploreEmptyState({
			t,
			locale: i18n.language,
			hiddenLessons: totalLessons - visibleLessons,
			hasSearchQuery: Boolean(searchQuery),
			filterState,
		});
	}, [filterState, i18n.language, searchQuery, sections, t, totalLessons]);

	const viewabilityConfig = useMemo(
		() => ({
			itemVisiblePercentThreshold: 50,
			minimumViewTime: 1000,
		}),
		[],
	);

	return (
		<SectionList
			sections={sections}
			initialNumToRender={10}
			style={{ flex: 1, width: "100%" }}
			contentContainerStyle={contentContainerStyle}
			maxToRenderPerBatch={5}
			windowSize={5}
			updateCellsBatchingPeriod={100}
			removeClippedSubviews
			stickySectionHeadersEnabled={false}
			keyExtractor={keyExtractor}
			renderSectionHeader={renderSectionHeader}
			renderItem={renderItem}
			viewabilityConfig={viewabilityConfig}
			getItemLayout={(_, index) => ({
				length: ROW_HEIGHT,
				offset: ROW_HEIGHT * index,
				index,
			})}
			ListEmptyComponent={wrapConstrainedContent(
				<Box paddingBottom={24}>
					<EmptyState
						title={emptyState.title}
						subtitle={emptyState.subtitle}
						cta={emptyState.cta}
						handleAction={onClearFilters}
					/>
				</Box>,
			)}
		/>
	);
};
